import { Alert, AlertDescription } from "@repo/ui/alert";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@repo/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Loader2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { fetchRecentAuthenticityChecks, runAuthenticityBatch } from "@/lib/api";
import { JevCheckBadge } from "./JevCheckBadge";
import { JEV_CHECKS_KEY } from "./useJevChecks";

const RECENT_LIMIT = 50;
const BATCH_SINCE_MINUTES = 60;

function formatCost(cost: number | null | undefined): string {
  return typeof cost === "number" ? `$${cost.toFixed(5)}` : "—";
}

/** The last 50 Jev checks across all snapshots, so a check run can be reviewed. */
export function RecentJevChecks() {
  const { canManageJobs } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const recentQuery = useQuery({
    queryKey: [...JEV_CHECKS_KEY, "recent", RECENT_LIMIT],
    queryFn: () => fetchRecentAuthenticityChecks(RECENT_LIMIT),
  });
  const batchMutation = useMutation({
    mutationFn: () => runAuthenticityBatch({ sinceMinutes: BATCH_SINCE_MINUTES }),
    onSuccess: ({ summary }) =>
      toast({
        title: `Jev batch: ${summary.checked} checked`,
        description: `${summary.candidates} candidates, ${summary.selected} selected, ${summary.errors} errors, ${summary.deferred} deferred, ${formatCost(summary.cost)}`,
      }),
    onError: (err) =>
      toast({
        title: "Jev batch failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: JEV_CHECKS_KEY }),
  });
  const checks = recentQuery.data ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Recent Jev checks
          </CardTitle>
          <CardDescription>
            The last {RECENT_LIMIT} LLM authenticity checks (snapshot-authenticity-check). Hover a
            badge for the model's reasons or the error.
          </CardDescription>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => recentQuery.refetch()}
            disabled={recentQuery.isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${recentQuery.isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => batchMutation.mutate()}
            disabled={!canManageJobs || batchMutation.isPending}
            title="Runs the selection policy over snapshots created in the last 60 minutes"
          >
            {batchMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Run policy batch (last {BATCH_SINCE_MINUTES} min)
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {recentQuery.error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              Unable to load Jev checks:{" "}
              {recentQuery.error instanceof Error ? recentQuery.error.message : "Unknown error"}
            </AlertDescription>
          </Alert>
        )}
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Checked</TableHead>
                <TableHead>Snapshot</TableHead>
                <TableHead>Jev</TableHead>
                <TableHead>Model</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {checks.map((check) => {
                const snapshot = check.property_snapshots;
                const label = `${check.property_snapshot_id.slice(0, 8)}${snapshot?.adapter_slug ? ` · ${snapshot.adapter_slug}` : ""}`;
                return (
                  <TableRow key={check.id}>
                    <TableCell className="whitespace-nowrap text-xs">
                      {new Date(check.checked_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {snapshot ? (
                        <Link
                          to={`/dashboard/properties/${snapshot.property_id}`}
                          className="text-primary underline-offset-2 hover:underline"
                          title={check.property_snapshot_id}
                        >
                          {label}
                        </Link>
                      ) : (
                        label
                      )}
                    </TableCell>
                    <TableCell>
                      <JevCheckBadge check={check} />
                    </TableCell>
                    <TableCell className="text-xs">{check.model}</TableCell>
                    <TableCell className="text-right text-xs">
                      {formatCost(check.usage?.cost)}
                      {(check.usage?.attempts ?? 1) > 1 && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({check.usage?.attempts} calls)
                        </span>
                      )}
                    </TableCell>
                    <TableCell
                      className="max-w-72 truncate text-xs text-destructive"
                      title={check.error ?? undefined}
                    >
                      {check.error ?? ""}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!recentQuery.isLoading && checks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                    No Jev checks yet.
                  </TableCell>
                </TableRow>
              )}
              {recentQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
